import type { CsvRow, SkippedRow, ValidateRowsResult } from "../types/draft";

export function validateHeaders(row: Record<string, unknown> | null | undefined, csvHeaders: string[]): string[] {
  const uploadedHeaders = Object.keys(row || {});
  return csvHeaders.filter(h => !uploadedHeaders.includes(h));
}

const VALID_POSITIONS = new Set(["QB", "RB", "WR", "TE", "K", "DST"]);
const REQUIRED_ROW_FIELDS = [
  "First Name",
  "Last Name",
  "Position",
  "Pick Number",
  "Draft",
  "Tournament Title",
];

/**
 * Validates each row of parsed CSV data.
 * Returns { validRows, skippedRows } where skippedRows is an array of
 * { rowIndex, reason } objects.
 */
export function validateRows(rows: Record<string, string>[]): ValidateRowsResult {
  const validRows: CsvRow[] = [];
  const skippedRows: SkippedRow[] = [];

  rows.forEach((row, idx) => {
    const rowNum = idx + 2; // 1-based, +1 for header

    // Check required fields are non-empty
    const missingFields = REQUIRED_ROW_FIELDS.filter(
      f => !row[f] || String(row[f]).trim() === ""
    );
    if (missingFields.length > 0) {
      skippedRows.push({
        rowIndex: rowNum,
        reason: `Missing required field(s): ${missingFields.join(", ")}`,
      });
      return;
    }

    // Pick Number must be a positive integer
    const pick = Number(row["Pick Number"]);
    if (!Number.isInteger(pick) || pick < 1) {
      skippedRows.push({
        rowIndex: rowNum,
        reason: `Invalid Pick Number: "${row["Pick Number"]}"`,
      });
      return;
    }

    // Position must be recognized
    const pos = String(row["Position"]).trim().toUpperCase();
    if (!VALID_POSITIONS.has(pos)) {
      skippedRows.push({
        rowIndex: rowNum,
        reason: `Unrecognized Position: "${row["Position"]}"`,
      });
      return;
    }

    validRows.push({ ...row, Position: pos } as CsvRow);
  });

  return { validRows, skippedRows };
}

/**
 * Summarize skipped rows into a human-readable string for display.
 */
export function summarizeSkippedRows(skippedRows: SkippedRow[] | null | undefined): string | null {
  if (!skippedRows || skippedRows.length === 0) return null;
  const reasonCounts: Record<string, number> = {};
  skippedRows.forEach(({ reason }) => {
    // Normalize reason to a category
    const category = reason.startsWith("Missing")
      ? "Missing required fields"
      : reason.startsWith("Invalid Pick")
      ? "Invalid pick number"
      : reason.startsWith("Unrecognized Position")
      ? "Unrecognized position"
      : "Other";
    reasonCounts[category] = (reasonCounts[category] || 0) + 1;
  });
  const parts = Object.entries(reasonCounts).map(
    ([cat, count]) => `${count} row${count > 1 ? "s" : ""} — ${cat}`
  );
  return `Skipped ${skippedRows.length} row${skippedRows.length > 1 ? "s" : ""}: ${parts.join("; ")}`;
}